import React, { useMemo, useState } from "react";
import { scaleLinear } from "d3-scale";
import { area, line } from "d3-shape";
import {
	Figure, SURFACE, Tooltip, TooltipRow, XAxis, YGrid, formatNumber, seriesColor, useWidth,
} from "./shared";

const HEIGHT = 300;
const MARGIN = { top: 28, right: 150, bottom: 50, left: 48 };
const LABEL_GAP = 18;

// Means over time with 95% CI bands. Props (from Rails):
//   number, title, note, xLabel, yLabel, yDomain [min,max], yTicks, decimals,
//   series: [{ name, points: [{ x, y, lo, hi }] }]
export default function LineChart({ number, title, note, xLabel, yLabel, yDomain, yTicks, decimals = 1, series }) {
	const [ref, width] = useWidth();
	const [active, setActive] = useState(null);

	const narrow = width < 560;
	const margin = { ...MARGIN, right: narrow ? 16 : MARGIN.right };
	const innerW = width - margin.left - margin.right;
	const innerH = HEIGHT - margin.top - margin.bottom;
	const xs = series[0].points.map((p) => p.x);

	const x = scaleLinear().domain([xs[0], xs[xs.length - 1]]).range([margin.left, margin.left + innerW]);
	const y = scaleLinear().domain(yDomain).range([margin.top + innerH, margin.top]);

	const linePath = line().x((p) => x(p.x)).y((p) => y(p.y));
	const bandPath = area().x((p) => x(p.x)).y0((p) => y(p.lo)).y1((p) => y(p.hi));

	// End labels: spread apart when lines converge, with leader lines back to the line end.
	const endLabels = useMemo(() => {
		const labels = series
			.map((s, i) => {
				const last = s.points[s.points.length - 1];
				return { i, name: s.name, value: last.y, lineY: y(last.y), labelY: y(last.y) };
			})
			.sort((a, b) => a.lineY - b.lineY);
		for (let k = 1; k < labels.length; k++) {
			labels[k].labelY = Math.max(labels[k].labelY, labels[k - 1].labelY + LABEL_GAP);
		}
		return labels;
	}, [series, y]);

	function indexFromPointer(event) {
		const svgX = event.clientX - event.currentTarget.getBoundingClientRect().left;
		let best = 0;
		xs.forEach((value, i) => {
			if (Math.abs(x(value) - svgX) < Math.abs(x(xs[best]) - svgX)) best = i;
		});
		return best;
	}

	function onKeyDown(event) {
		if (event.key === "ArrowRight") setActive((a) => Math.min(xs.length - 1, (a ?? -1) + 1));
		else if (event.key === "ArrowLeft") setActive((a) => Math.max(0, (a ?? xs.length) - 1));
		else if (event.key === "Escape") setActive(null);
		else return;
		event.preventDefault();
	}

	const table = {
		columns: [xLabel, ...series.flatMap((s) => [`${s.name} M`, `${s.name} 95% CI`])],
		rows: xs.map((xv, i) => [
			xv,
			...series.flatMap((s) => [
				formatNumber(s.points[i].y, decimals),
				`[${formatNumber(s.points[i].lo, decimals)}, ${formatNumber(s.points[i].hi, decimals)}]`,
			]),
		]),
	};

	return (
		<Figure
			number={number}
			title={title}
			note={note}
			table={table}
			legend={series.map((s, i) => ({ name: s.name, color: seriesColor(i) }))}
		>
			<div ref={ref} className="relative">
				<svg
					width={width}
					height={HEIGHT}
					role="group"
					aria-label={`Figure ${number}: ${title}. Use left and right arrow keys to read values.`}
					tabIndex={0}
					className="block overflow-visible font-sans focus-visible:outline-offset-4"
					onPointerMove={(e) => setActive(indexFromPointer(e))}
					onPointerLeave={() => setActive(null)}
					onKeyDown={onKeyDown}
					onBlur={() => setActive(null)}
				>
					<text x={margin.left - 40} y={12} fontSize="12.5" fill="var(--color-gray-700)">{yLabel}</text>
					<YGrid scale={y} ticks={yTicks} width={innerW} left={margin.left} />
					<XAxis scale={x} ticks={xs} y={margin.top + innerH} label={xLabel} width={innerW} left={margin.left} />

					{series.map((s, i) => (
						<path key={`band-${s.name}`} d={bandPath(s.points)} fill={seriesColor(i)} fillOpacity="0.1" />
					))}
					{series.map((s, i) => (
						<path
							key={`line-${s.name}`}
							d={linePath(s.points)}
							fill="none"
							stroke={seriesColor(i)}
							strokeWidth="2"
							strokeLinejoin="round"
							strokeLinecap="round"
						/>
					))}

					{active !== null && (
						<line
							x1={x(xs[active])} x2={x(xs[active])}
							y1={margin.top} y2={margin.top + innerH}
							stroke="var(--color-gray-500)" strokeWidth="1" shapeRendering="crispEdges"
						/>
					)}

					{series.map((s, i) =>
						s.points.map((p, k) => (
							<circle
								key={`pt-${s.name}-${k}`}
								cx={x(p.x)} cy={y(p.y)}
								r={active === k ? 5.5 : 4}
								fill={seriesColor(i)}
								stroke={SURFACE}
								strokeWidth="2"
							/>
						)),
					)}

					{!narrow && endLabels.map((l) => (
						<g key={`label-${l.name}`}>
							{Math.abs(l.labelY - l.lineY) > 2 && (
								<path
									d={`M${margin.left + innerW + 8},${l.lineY} L${margin.left + innerW + 18},${l.labelY}`}
									stroke="var(--color-gray-400)" strokeWidth="1" fill="none"
								/>
							)}
							<text x={margin.left + innerW + 22} y={l.labelY} dy="0.32em" fontSize="12.5" fill="var(--color-ink)">
								<tspan fontWeight="600" className="dp-figures">{formatNumber(l.value, decimals)}</tspan>
								<tspan dx="6" fill="var(--color-gray-600)">{l.name}</tspan>
							</text>
						</g>
					))}
				</svg>

				{active !== null && (
					<Tooltip x={x(xs[active])} y={margin.top} containerWidth={width}>
						<div className="mb-1 text-xs font-semibold text-gray-600">{xLabel} {xs[active]}</div>
						{series.map((s, i) => (
							<TooltipRow
								key={s.name}
								color={seriesColor(i)}
								label={s.name}
								value={`${formatNumber(s.points[active].y, decimals)} [${formatNumber(s.points[active].lo, decimals)}, ${formatNumber(s.points[active].hi, decimals)}]`}
							/>
						))}
					</Tooltip>
				)}
			</div>
		</Figure>
	);
}
